import React, { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { useEventId } from "../EventContext";
import { useAuth, Button, InputField } from "@vionixasia/shared-components";
import { useNotification } from "@vionixasia/shared-components/common/NotificationContext";

/**
 * Same wrapper AdminTiming and InputNinja use, so fields here read like every
 * other form in livetiming rather than carrying their own one-off styling.
 */
const FormField = forwardRef(
  ({ className = "", wrapperClassName = "", ...props }, ref) => (
    <InputField
      ref={ref}
      labelColor="text-slate-700"
      className={className}
      wrapperClassName={wrapperClassName}
      {...props}
    />
  ),
);

/**
 * Start control for individual-start events.
 *
 * On an all-day individual event the mat cannot tell "setting off" from
 * "wandering past" — a chip read at the start line is the same signal either
 * way. Timing rules alone therefore cannot decide which crossing is a runner's
 * real departure, and a test session at 14:41 was once accepted as the start of
 * a 14:50 race.
 *
 * So the operator names who is going: tick the runners, press START as they
 * leave. That press is applied immediately as their start time, so nobody is
 * left without a clock if their chip is never read. Each runner's own next mat
 * crossing then refines their start to their own step across the line, and
 * locks it — later reads (walking back past the mat) cannot move it.
 */

const API = import.meta.env.VITE_API_BASE_URL;

const clockOf = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
};

export const StartWave = () => {
  const { realEventId: eventId } = useEventId();
  const { getIdToken } = useAuth();
  const { showNotification } = useNotification();

  const [participants, setParticipants] = useState([]);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const authenticatedFetch = useCallback(
    async (url, options = {}) => {
      const token = await getIdToken();
      return fetch(url, {
        ...options,
        headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
      });
    },
    [getIdToken],
  );

  useEffect(() => {
    if (!eventId) return;
    fetch(`${API}/api/timing/participants?eventId=${encodeURIComponent(eventId)}&limit=2000`)
      .then((r) => r.json())
      .then((d) => setParticipants(d?.participants || d || []))
      .catch(() => setParticipants([]));
  }, [eventId]);

  const loadResults = useCallback(() => {
    if (!eventId) return;
    fetch(`${API}/api/timing/results?eventId=${encodeURIComponent(eventId)}&limit=2000`)
      .then((r) => r.json())
      .then((d) => setResults(d?.results || []))
      .catch(() => {});
  }, [eventId]);

  useEffect(() => loadResults(), [loadResults]);

  const resultByBib = useMemo(() => {
    const m = new Map();
    results.forEach((r) => m.set(String(r.bib), r));
    return m;
  }, [results]);

  // Every participant, not split by category: a wave is whoever is standing on
  // the line, which can cut across categories.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return participants
      .filter((p) => {
        if (!q) return true;
        return (
          String(p.bib).toLowerCase().includes(q) ||
          String(p.name || "").toLowerCase().includes(q)
        );
      })
      .sort((a, b) => String(a.bib).localeCompare(String(b.bib), undefined, { numeric: true }));
  }, [participants, search]);

  const toggle = (bib) =>
    setSelected((prev) => {
      const next = new Set(prev);
      const key = String(bib);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const toggleAllVisible = () =>
    setSelected((prev) => {
      const visible = rows.map((p) => String(p.bib));
      const allOn = visible.every((b) => prev.has(b));
      const next = new Set(prev);
      visible.forEach((b) => (allOn ? next.delete(b) : next.add(b)));
      return next;
    });

  const startWave = async () => {
    if (selected.size === 0) {
      showNotification("error", "Tick the runners going off first.");
      return;
    }
    setSaving(true);
    try {
      const res = await authenticatedFetch(`${API}/api/timing/start-wave`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ eventId, bibs: Array.from(selected) }),
      });
      const data = await res.json();
      if (!res.ok) {
        showNotification("error", data?.message || "Failed to start wave");
      } else {
        showNotification(
          "success",
          `${data.armed?.length ?? 0} runner(s) started at ${clockOf(data.startedAt)}${
            data.notFound?.length ? ` · not found: ${data.notFound.join(", ")}` : ""
          }`,
        );
        setSelected(new Set());
        loadResults();
      }
    } catch (error) {
      showNotification("error", error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    // Same shell as the results page: this sits inside the app Layout, so the
    // navbar is already above it and the content only needs the shared
    // container width.
    <section className="min-h-screen mt-10 bg-white mb-10">
      <div className="container mx-auto px-4">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900 font-oxanium">Start control</h1>
          <p className="mt-1 text-sm text-slate-500">
            Tick the runners going off, then press START as they leave. That moment becomes
            their start time; their own mat crossing refines it a few seconds later.
          </p>
        </header>

        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <FormField
            label="Search"
            name="start-search"
            labelClassName="sr-only"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search BIB or name"
            wrapperClassName="w-full sm:max-w-xs"
          />
          <div className="flex items-center gap-2">
            <Button onClick={toggleAllVisible} variant="ghost" color="cyan">
              Select all shown
            </Button>
            <Button
              onClick={startWave}
              disabled={saving || selected.size === 0}
              color="cyan"
            >
              {saving ? "Starting…" : `START (${selected.size})`}
            </Button>
          </div>
        </div>

        {/* Desktop table — mirrors the results table: separated rows as cards,
            slate-50 header, rounded end cells. */}
        <div className="hidden md:block overflow-x-auto pb-8">
          <table className="min-w-full border-separate table-auto" style={{ borderSpacing: "0 8px" }}>
            <thead>
              <tr>
                <th className="bg-slate-50 px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase tracking-wider w-[70px] rounded-l-card border-y border-l border-slate-200">
                  Go
                </th>
                <th className="bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-[110px] border-y border-slate-200">
                  BIB
                </th>
                <th className="bg-slate-50 px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider border-y border-slate-200">
                  Name
                </th>
                <th className="bg-slate-50 px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider rounded-r-card border-y border-r border-slate-200">
                  Start
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="bg-white px-4 py-6 text-center text-sm text-slate-500 rounded-card border border-slate-200">
                    No participants
                  </td>
                </tr>
              ) : (
                rows.map((p) => {
                  const key = String(p.bib);
                  const r = resultByBib.get(key);
                  const started = r?.startMatTime;
                  const fromOperator = !!r?.startFromOperator;
                  return (
                    <tr key={key} className="group cursor-pointer" onClick={() => toggle(key)}>
                      <td className="bg-white px-4 py-4 text-center rounded-l-card border-y border-l border-slate-200 group-hover:bg-slate-50 transition-colors">
                        <input
                          type="checkbox"
                          checked={selected.has(key)}
                          onChange={() => toggle(key)}
                          onClick={(e) => e.stopPropagation()}
                          className="h-4 w-4 accent-cyan-600"
                        />
                      </td>
                      <td className="bg-white px-4 py-4 text-sm font-semibold tabular-nums text-slate-900 border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                        {p.bib}
                      </td>
                      <td className="bg-white px-4 py-4 text-sm font-bold uppercase text-slate-900 border-y border-slate-200 group-hover:bg-slate-50 transition-colors">
                        {p.name}
                      </td>
                      <td className="bg-white px-4 py-4 text-right text-sm rounded-r-card border-y border-r border-slate-200 group-hover:bg-slate-50 transition-colors whitespace-nowrap">
                        {started ? (
                          <span className={fromOperator ? "text-amber-700" : "text-emerald-700"}>
                            <span className="tabular-nums">{clockOf(started)}</span>
                            <span className="ml-1 text-xs">{fromOperator ? "operator" : "mat"}</span>
                          </span>
                        ) : (
                          <span className="text-slate-400">not started</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards — the results page switches to cards below md too. */}
        <div className="md:hidden space-y-3 pb-6">
          {rows.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-500 bg-white rounded-card border border-slate-200">
              No participants
            </div>
          ) : (
            rows.map((p) => {
              const key = String(p.bib);
              const r = resultByBib.get(key);
              const started = r?.startMatTime;
              const fromOperator = !!r?.startFromOperator;
              return (
                <label
                  key={`m-${key}`}
                  className="flex items-center gap-3 bg-white rounded-card border border-slate-200 shadow-sm p-3"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(key)}
                    onChange={() => toggle(key)}
                    className="h-4 w-4 shrink-0 accent-cyan-600"
                  />
                  <span className="w-14 shrink-0 text-sm font-semibold tabular-nums text-slate-900">
                    {p.bib}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm font-bold uppercase text-slate-900">
                    {p.name}
                  </span>
                  <span
                    className={`shrink-0 text-xs tabular-nums ${
                      started ? (fromOperator ? "text-amber-700" : "text-emerald-700") : "text-slate-400"
                    }`}
                  >
                    {started ? clockOf(started) : "not started"}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
};

export default StartWave;
